/**
 * Customer360Timeline — Amplitude-inspired 3-column layout.
 *
 * Left: Profile card + properties
 * Center: Chronological interaction timeline (cases, orders, shipments)
 * Right: Detail panel (shows when an item is selected)
 */
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { CaseLink, CustomerProfileLink, TrackingLink } from './CaseLink';
import { CaseSummary } from './CaseSummary';
import type { CustomerProfile } from './Customer360.types';
import { type MergedCustomer360, type ShipmentItem, toViewModel } from './Customer360.viewmodel';
import { AssetsSection, CasesSection, OrdersSection, ShipmentsSection } from './Customer360Panel';
import { DateTimeDisplay } from './DateTimeDisplay';

// =============================================================================
// Timeline event — unified type for all interactions
// =============================================================================

interface TimelineEvent {
  id: string;
  type: 'case' | 'shipment' | 'device' | 'order' | 'asset';
  title: string;
  timestamp: string;
  status?: string;
  brand?: string;
  detail: Record<string, unknown>;
}

function buildTimeline(data: MergedCustomer360): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  data.cases.forEach((c) => {
    events.push({
      id: c.caseId,
      type: 'case',
      title: c.title,
      timestamp: c.createdAt,
      status: c.status,
      brand: c.brand,
      detail: c as unknown as Record<string, unknown>,
    });
  });

  // Group shipments by order
  const shipmentGroups = new Map<string, ShipmentItem[]>();
  data.shipments.forEach((s) => {
    const key = String(s.orderNumber);
    if (!shipmentGroups.has(key)) shipmentGroups.set(key, []);
    shipmentGroups.get(key)!.push(s);
  });
  shipmentGroups.forEach((items, orderNum) => {
    events.push({
      id: `shipment-${orderNum}`,
      type: 'shipment',
      title: `Shipment #${orderNum} — ${items.length} item${items.length > 1 ? 's' : ''}`,
      timestamp: items[0].orderDate,
      detail: { orderNumber: orderNum, items } as unknown as Record<string, unknown>,
    });
  });

  data.orders.forEach((o) => {
    events.push({
      id: o.orderNumber,
      type: 'order',
      title: `Order ${o.orderNumber}`,
      timestamp: o.date,
      status: o.status,
      detail: o as unknown as Record<string, unknown>,
    });
  });

  data.connectedDevices.forEach((d) => {
    if (d.lastConnected) {
      events.push({
        id: d.controllerSerial,
        type: 'device',
        title: `${d.tradeName} connected`,
        timestamp: d.lastConnected,
        detail: d as unknown as Record<string, unknown>,
      });
    }
  });

  // Products (assets) — anchor to registeredDate/purchaseDate/lastConnected
  // so clicking a product in the Records tab resolves to a timeline event
  // that DetailPanel can render. Skip rows with no date at all (rare).
  data.assets.forEach((a) => {
    const ts = a.registeredDate ?? a.purchaseDate ?? a.lastConnected ?? null;
    if (!ts) return;
    const titleSuffix = a.nickname ? ` — ${a.nickname}` : '';
    events.push({
      id: a.serialNumber,
      type: 'asset',
      title: `${a.productName || 'Product'} registered${titleSuffix}`,
      timestamp: ts,
      status: a.status,
      detail: a as unknown as Record<string, unknown>,
    });
  });

  // Sort newest first
  events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  return events;
}

// =============================================================================
// Helpers
// =============================================================================

function formatDate(d: string): string {
  // `new Date("YYYY-MM-DD")` parses as UTC midnight, then renders as the
  // previous day in any timezone west of UTC. Construct local midnight for
  // date-only inputs so the calendar date survives.
  let parsed: Date;
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [y, m, day] = d.split('-').map(Number);
    parsed = new Date(y, m - 1, day);
  } else {
    parsed = new Date(d);
  }
  return parsed.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTime(d: string): string {
  return new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

// `formatDateTime` is imported from DateTimeDisplay so all three layouts
// render timestamps identically.

function relativeDate(d: string): string {
  const diff = Date.now() - new Date(d).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

/** `April 14, 2026 · Today` — absolute date + relative in one string. */
function timeAgo(d: string): string {
  return `${formatDate(d)} · ${relativeDate(d)}`;
}

function formatCurrency(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

function daysSince(d: string | null | undefined): number | null {
  if (!d) return null;
  const t = new Date(d).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.floor((Date.now() - t) / 86400000);
}

// Contact icons for the sidebar (mirror Customer360Panel).
function MailIcon() {
  return (
    <svg
      style={sidebarIconStyle}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M22 7l-10 7L2 7" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg
      style={sidebarIconStyle}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
    </svg>
  );
}

function MapPinIcon() {
  return (
    <svg
      style={sidebarIconStyle}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

// Price-tag icon used in the Products sidebar cards.
function ProductTag({ size = 18, strokeWidth = 2 }: { size?: number; strokeWidth?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <g transform="translate(0.7 0.7)">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </g>
    </svg>
  );
}

const sidebarIconStyle: React.CSSProperties = {
  width: '14px',
  height: '14px',
  flexShrink: 0,
  color: '#9ca3af',
};

// =============================================================================
// Colors
// =============================================================================

// Cloudscape-aligned palette — see Customer360Panel for the rationale.
// Cool slate neutrals so the layout sits comfortably inside Amazon
// Connect Agent Workspace; status colors stay muted/dark for legibility.
const c = {
  border: '#ebebf0',
  borderLight: '#f2f3f3',
  bg: '#ffffff',
  bgMuted: '#f6f6f9',
  text: '#16191f',
  textSec: '#424650',
  textMuted: '#545b64',
  blue: '#006ce0',
  green: '#037f0c',
  red: '#d13212',
  amber: '#8c3b00',
  // No Cloudscape token for purple — keep the prior shipment accent.
  purple: '#8b5cf6',
};

const eventColors: Record<string, string> = {
  case: c.blue,
  shipment: c.purple,
  order: c.green,
  device: c.amber,
  asset: c.red,
};

const eventLabels: Record<string, string> = {
  case: 'Case',
  shipment: 'Shipment',
  order: 'Order',
  device: 'Device',
  asset: 'Product',
};

const statusColors: Record<string, { bg: string; fg: string }> = {
  open: { bg: '#fef3c7', fg: '#92400e' },
  closed: { bg: '#f3f4f6', fg: '#6b7280' },
  pending: { bg: '#dbeafe', fg: '#1e40af' },
  Delivered: { bg: '#dcfce7', fg: '#166534' },
  Processing: { bg: '#fef3c7', fg: '#92400e' },
  Active: { bg: '#dcfce7', fg: '#166534' },
};

// =============================================================================
// Styles
// =============================================================================

const s: Record<string, React.CSSProperties> = {
  container: {
    fontFamily: 'system-ui, -apple-system, sans-serif',
    display: 'flex',
    height: '100%',
    background: c.bg,
    overflow: 'hidden',
  },
  // -- Left sidebar --
  sidebar: {
    width: '240px',
    flexShrink: 0,
    borderRight: `1px solid ${c.border}`,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  profileSection: {
    padding: '20px 16px',
    textAlign: 'center',
    borderBottom: `1px solid ${c.border}`,
  },
  avatar: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    background: c.blue,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: 700,
    margin: '0 auto 8px',
  },
  profileName: {
    fontSize: '15px',
    fontWeight: 600,
    color: c.text,
    margin: '0 0 4px',
  },
  profileEmail: {
    fontSize: '12px',
    color: c.textSec,
  },
  dateRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    marginTop: '12px',
    padding: '8px 0',
    borderTop: `1px solid ${c.borderLight}`,
  },
  dateItem: {
    textAlign: 'center',
  },
  dateLabel: {
    fontSize: '10px',
    color: c.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  dateValue: {
    fontSize: '12px',
    fontWeight: 600,
    color: c.text,
    marginTop: '2px',
  },
  propsSection: {
    padding: '12px 16px',
    flex: 1,
  },
  propsSectionTitle: {
    fontSize: '10px',
    fontWeight: 600,
    color: c.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '8px',
  },
  propRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '4px 0',
    fontSize: '12px',
  },
  propLabel: {
    color: c.textMuted,
  },
  propValue: {
    color: c.text,
    fontWeight: 500,
    textAlign: 'right',
    maxWidth: '55%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // -- Richer sidebar (matches Customer360Panel exactly) --
  left: {
    width: '280px',
    flexShrink: 0,
    borderRight: `1px solid ${c.border}`,
    background: c.bg,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  profileCard: {
    padding: '16px',
    borderBottom: `1px solid ${c.border}`,
  },
  avatarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  avatarLarge: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: c.blue,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '15px',
    fontWeight: 700,
    flexShrink: 0,
  },
  profileNameInline: {
    fontSize: '15px',
    fontWeight: 600,
    color: c.text,
    margin: 0,
  },
  profileMeta: {
    fontSize: '11px',
    color: c.textMuted,
  },
  contactList: {
    marginTop: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  contactItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    color: c.textSec,
  },
  contactValue: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  mergedSection: {
    padding: '12px 16px',
    borderBottom: `1px solid ${c.border}`,
  },
  sectionLabel: {
    fontSize: '10px',
    fontWeight: 600,
    color: c.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '8px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    borderBottom: `1px solid ${c.border}`,
  },
  statCell: {
    padding: '10px 16px',
    borderRight: `1px solid ${c.borderLight}`,
    borderBottom: `1px solid ${c.borderLight}`,
  },
  statValue: {
    fontSize: '16px',
    fontWeight: 700,
    color: c.text,
  },
  statLabel: {
    fontSize: '10px',
    color: c.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    marginTop: '1px',
  },
  mergedId: {
    display: 'inline-block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '120px',
    verticalAlign: 'middle',
  },
  // -- Center timeline --
  timeline: {
    flex: 1,
    overflow: 'auto',
    borderRight: `1px solid ${c.border}`,
  },
  timelineHeader: {
    padding: '12px 16px',
    borderBottom: `1px solid ${c.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'sticky',
    top: 0,
    background: c.bg,
    zIndex: 1,
  },
  timelineTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: c.text,
    margin: 0,
  },
  timelineCount: {
    fontSize: '12px',
    color: c.textMuted,
  },
  dateGroup: {
    padding: '8px 16px 4px',
    fontSize: '11px',
    fontWeight: 600,
    color: c.textSec,
    background: c.bgMuted,
    position: 'sticky',
    top: '45px',
    zIndex: 1,
  },
  eventRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '8px 16px',
    cursor: 'pointer',
    borderBottom: `1px solid ${c.borderLight}`,
    transition: 'background 0.1s',
  },
  eventRowSelected: {
    background: '#eff6ff',
  },
  eventTime: {
    fontSize: '11px',
    color: c.textMuted,
    width: '55px',
    flexShrink: 0,
    paddingTop: '2px',
    fontFamily: 'monospace',
  },
  eventDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
    marginTop: '5px',
  },
  eventContent: {
    flex: 1,
    minWidth: 0,
  },
  eventTitle: {
    fontSize: '13px',
    color: c.text,
    lineHeight: 1.3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  eventMeta: {
    fontSize: '11px',
    color: c.textMuted,
    marginTop: '1px',
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
  },
  eventBadge: {
    fontSize: '10px',
    fontWeight: 600,
    padding: '1px 6px',
    borderRadius: '6px',
  },
  // -- Right detail panel --
  detailPanel: {
    width: '340px',
    flexShrink: 0,
    overflow: 'auto',
    background: c.bgMuted,
  },
  detailHeader: {
    padding: '12px 16px',
    borderBottom: `1px solid ${c.border}`,
    background: c.bg,
  },
  detailDate: {
    fontSize: '11px',
    color: c.textMuted,
  },
  detailTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: c.text,
    margin: '4px 0 0',
    lineHeight: 1.3,
  },
  detailBody: {
    padding: '12px 16px',
  },
  detailCard: {
    background: c.bg,
    borderRadius: '8px',
    border: `1px solid ${c.border}`,
    padding: '12px',
    marginBottom: '8px',
  },
  detailCardTitle: {
    fontSize: '10px',
    fontWeight: 600,
    color: c.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    marginBottom: '6px',
  },
  detailRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '10px',
    padding: '3px 0',
    fontSize: '12px',
  },
  detailLabel: {
    color: c.textMuted,
    flex: '0 0 auto',
    fontSize: '11px',
  },
  detailValue: {
    color: c.text,
    fontWeight: 500,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  detailEmpty: {
    padding: '40px 16px',
    textAlign: 'center',
    color: c.textMuted,
    fontSize: '13px',
  },
  summaryText: {
    fontSize: '12px',
    color: c.textSec,
    lineHeight: 1.5,
    marginBottom: '4px',
  },
};

// =============================================================================
// Shared row + copy helpers (mirror Customer360Panel so detail views look
// and behave the same in A: 2-Column and B: 3-Column).
// =============================================================================

function CopyButton({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button
      type="button"
      onClick={onClick}
      title={label ? `Copy ${label}` : 'Copy'}
      style={{
        background: 'transparent',
        border: 'none',
        padding: '0 2px',
        cursor: 'pointer',
        color: copied ? c.green : c.textMuted,
        display: 'inline-flex',
        alignItems: 'center',
      }}
    >
      {copied ? (
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
        </svg>
      )}
    </button>
  );
}

function DetailRow({
  label,
  value,
  copy,
  valueStyle,
  multiline,
}: {
  label: string;
  value: ReactNode;
  copy?: string;
  valueStyle?: React.CSSProperties;
  multiline?: boolean;
}) {
  const copyText = copy ?? (typeof value === 'string' ? value : undefined);
  const innerTextStyle: React.CSSProperties = multiline
    ? { whiteSpace: 'normal', wordBreak: 'break-all' }
    : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
  return (
    <div style={s.detailRow}>
      <span style={s.detailLabel}>{label}</span>
      {/* value-group: sizes to content so the copy button hugs the text
          instead of sitting at the row's trailing edge. Inner text
          truncates with ellipsis (or wraps when multiline). */}
      <span
        style={{
          color: c.text,
          fontWeight: 500,
          display: 'inline-flex',
          alignItems: 'baseline',
          gap: '4px',
          minWidth: 0,
          flex: '0 1 auto',
          ...valueStyle,
        }}
      >
        <span style={innerTextStyle}>{value}</span>
        {copyText && <CopyButton text={copyText} label={label} />}
      </span>
    </div>
  );
}

// =============================================================================
// Detail Panel
// =============================================================================

function DetailPanel({ event }: { event: TimelineEvent | null }) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    panelRef.current?.scrollTo(0, 0);
  }, [event?.id]);

  if (!event) {
    return (
      <div style={s.detailPanel}>
        <div style={s.detailEmpty}>Select an event to view details</div>
      </div>
    );
  }

  const d = event.detail;

  return (
    <div style={s.detailPanel} ref={panelRef}>
      <div style={s.detailHeader}>
        <div style={s.detailDate}>
          {formatDate(event.timestamp)} &middot; {formatTime(event.timestamp)}
        </div>
        <div style={s.detailTitle}>{event.title}</div>
        <div style={{ marginTop: '6px', display: 'flex', gap: '6px' }}>
          <span
            style={{
              ...s.eventBadge,
              background: eventColors[event.type] + '20',
              color: eventColors[event.type],
            }}
          >
            {eventLabels[event.type]}
          </span>
          {event.status &&
            (() => {
              const sc = statusColors[event.status] || { bg: '#f3f4f6', fg: '#6b7280' };
              return (
                <span style={{ ...s.eventBadge, background: sc.bg, color: sc.fg }}>
                  {event.status}
                </span>
              );
            })()}
        </div>
      </div>

      <div style={s.detailBody}>
        {/* Summary */}
        {!!d.summary && (
          <div style={s.detailCard}>
            <div style={s.detailCardTitle}>Summary</div>
            <CaseSummary source={String(d.summary)} style={s.summaryText} />
          </div>
        )}

        {/* Parts ordered */}
        {!!d.productsOrdered && (
          <div style={s.detailCard}>
            <div style={s.detailCardTitle}>Parts Ordered</div>
            {String(d.productsOrdered)
              .split(';')
              .map((part, i) => (
                <div key={i} style={{ fontSize: '12px', color: c.textSec, padding: '2px 0' }}>
                  • {part.trim()}
                </div>
              ))}
          </div>
        )}

        {/* Shipment items */}
        {!!(d.items && Array.isArray(d.items) && event.type === 'shipment') && (
          <div style={s.detailCard}>
            <div style={s.detailCardTitle}>Items</div>
            {(d.items as ShipmentItem[]).map((item, i) => (
              <div
                key={i}
                style={{
                  padding: '4px 0',
                  borderBottom:
                    i < (d.items as ShipmentItem[]).length - 1
                      ? `1px solid ${c.borderLight}`
                      : 'none',
                }}
              >
                <div style={{ fontSize: '12px', color: c.text, fontWeight: 500 }}>
                  {item.productDescription}
                </div>
                <div style={{ fontSize: '11px', color: c.textMuted }}>
                  SKU: {item.productSku} &middot; Qty: {item.quantity || '1'}
                  {item.trackingNumber && (
                    <>
                      {' '}
                      &middot; Tracking:{' '}
                      <TrackingLink
                        shipperName={item.shipperName}
                        trackingNumber={item.trackingNumber}
                      />
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Order items */}
        {!!(d.items && Array.isArray(d.items) && event.type === 'order') && (
          <div style={s.detailCard}>
            <div style={s.detailCardTitle}>Items</div>
            {(d.items as string[]).map((item, i) => (
              <div key={i} style={{ fontSize: '12px', color: c.textSec, padding: '2px 0' }}>
                • {item}
              </div>
            ))}
          </div>
        )}

        {/* Properties */}
        <div style={s.detailCard}>
          <div style={s.detailCardTitle}>Properties</div>
          {event.brand && <DetailRow label="Brand" value={event.brand} />}
          {!!d.asset && <DetailRow label="Product" value={String(d.asset)} />}
          {!!d.referenceNumber && (
            <DetailRow
              label="Ref #"
              value={
                event.type === 'case' ? (
                  <CaseLink caseId={event.id}>{String(d.referenceNumber)}</CaseLink>
                ) : (
                  String(d.referenceNumber)
                )
              }
              copy={String(d.referenceNumber)}
            />
          )}
          {!!d.orderNumber && <DetailRow label="Order #" value={String(d.orderNumber)} />}
          <DetailRow
            label="Created"
            value={<DateTimeDisplay value={event.timestamp} />}
            copy={event.timestamp}
          />
          {!!d.lastUpdated && (
            <DetailRow
              label="Updated"
              value={<DateTimeDisplay value={String(d.lastUpdated)} />}
              copy={String(d.lastUpdated)}
            />
          )}
          {event.type === 'case' && !!d.loupeLink && (
            <DetailRow
              label="Loupe"
              value={
                <a
                  href={String(d.loupeLink)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: c.blue, textDecoration: 'underline' }}
                >
                  Open scan results
                </a>
              }
              copy={String(d.loupeLink)}
            />
          )}
          {event.type === 'case' && !!d.agentName && (
            <DetailRow label="Agent" value={String(d.agentName)} />
          )}
          {event.type === 'case' && !!d.bpo && <DetailRow label="BPO" value={String(d.bpo)} />}
          {event.type === 'case' && !!d.site && <DetailRow label="Site" value={String(d.site)} />}
          <DetailRow
            label="ID"
            value={event.id}
            valueStyle={{ fontFamily: 'monospace' }}
            multiline
          />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

// Devices tab merged into Products elsewhere; this layout doesn't surface
// products as their own tab today, so the device row drops out entirely.
// Records rolls Cases / Shipments / Orders / Products into a single
// scrollable tab (matches A: 2-Column) so the agent scans all resources
// at once instead of tab-hopping. Cooks stays separate because it has
// its own table shape; this layout doesn't render cooks at all today.
// TODO: Traeger-specific — cooks and grill references. Generalize.
type CenterTab = 'timeline' | 'records';

export function Customer360Timeline({ data: profile }: { data: CustomerProfile }) {
  const data = toViewModel(profile);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [centerTab, setCenterTab] = useState<CenterTab>('timeline');
  const events = buildTimeline(data);

  // Clicks in the Records sections select the row instead of expanding
  // it inline — this layout has a dedicated right-hand detail panel,
  // so inline expansion is redundant. The sections call back with a
  // namespaced key (`case:<caseId>`, `ship:<orderNum>`, …); translate
  // each to the matching TimelineEvent id so DetailPanel renders.
  const handleRecordClick = (key: string) => {
    const colon = key.indexOf(':');
    if (colon < 0) return;
    const ns = key.slice(0, colon);
    const id = key.slice(colon + 1);
    let targetId: string | null = null;
    if (ns === 'case') targetId = id;
    else if (ns === 'ship') targetId = `shipment-${id}`;
    else if (ns === 'order') targetId = id;
    else if (ns === 'asset') targetId = id;
    if (!targetId) return;
    setSelectedId(selectedId === targetId ? null : targetId);
  };
  // Sections read expansion state via this set; we keep it empty so
  // nothing ever renders inline-expanded in the Records tab.
  const EMPTY_EXPANSION: Set<string> = new Set();
  const selectedEvent = events.find((e) => e.id === selectedId) || null;

  const id = data.identity;
  const initials = id.name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  // Group events by date
  const grouped = new Map<string, TimelineEvent[]>();
  events.forEach((e) => {
    const date = formatDate(e.timestamp);
    if (!grouped.has(date)) grouped.set(date, []);
    grouped.get(date)!.push(e);
  });

  return (
    <div style={s.container}>
      {/* ===== LEFT SIDEBAR (mirrors A: 2-Column) ===== */}
      <div style={s.left}>
        {/* Profile card */}
        <div style={s.profileCard}>
          <div style={s.avatarRow}>
            <div style={s.avatarLarge}>{initials}</div>
            <div>
              <h2 style={s.profileNameInline}>
                {id.primaryProfileId ? (
                  <CustomerProfileLink profileId={id.primaryProfileId} style={{ color: 'inherit' }}>
                    {id.name}
                  </CustomerProfileLink>
                ) : (
                  id.name
                )}
              </h2>
              <div style={s.profileMeta}>
                {id.accountType || 'Individual'}
                {id.accountStatus && (
                  <span
                    style={{
                      marginLeft: '6px',
                      display: 'inline-block',
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: id.accountStatus === 'Active' ? c.green : c.textMuted,
                      verticalAlign: 'middle',
                    }}
                  />
                )}
                {id.accountStatus && <span style={{ marginLeft: '4px' }}>{id.accountStatus}</span>}
              </div>
            </div>
          </div>

          <div style={s.contactList}>
            {id.emails.map((email, i) => (
              <div key={i} style={s.contactItem}>
                <MailIcon />
                <span style={s.contactValue}>{email}</span>
              </div>
            ))}
            {id.phones.map((phone, i) => (
              <div key={i} style={s.contactItem}>
                <PhoneIcon />
                <span style={s.contactValue}>{phone}</span>
              </div>
            ))}
            {id.address && (
              <div style={{ ...s.contactItem, alignItems: 'flex-start' }}>
                <MapPinIcon />
                <div
                  style={{
                    ...s.contactValue,
                    whiteSpace: 'normal',
                    overflow: 'visible',
                    textOverflow: 'clip',
                    lineHeight: 1.4,
                  }}
                >
                  {id.address}
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(id.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'block',
                      marginTop: '4px',
                      fontSize: '11px',
                      color: c.blue,
                      textDecoration: 'underline',
                    }}
                  >
                    Open in Google Maps
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Additional identity attrs */}
        {(id.accountNumber || id.businessName || id.personalEmail) && (
          <div style={s.mergedSection}>
            <div style={s.sectionLabel}>Additional details</div>
            <div style={{ fontSize: '11px', color: c.textSec, lineHeight: 1.6 }}>
              {id.accountNumber && (
                <div>
                  <span style={{ color: c.textMuted }}>Account #:</span>{' '}
                  <span style={{ fontFamily: 'monospace' }}>{id.accountNumber}</span>
                </div>
              )}
              {id.businessName && (
                <div>
                  <span style={{ color: c.textMuted }}>Business name:</span> {id.businessName}
                </div>
              )}
              {id.personalEmail && (
                <div>
                  <span style={{ color: c.textMuted }}>Personal email:</span> {id.personalEmail}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Salesforce IDs */}
        {(id.sfAccountId || id.sfContactId) && (
          <div style={s.mergedSection}>
            <div style={s.sectionLabel}>Salesforce</div>
            <div
              style={{
                fontSize: '11px',
                color: c.textMuted,
                fontFamily: 'monospace',
                lineHeight: 1.6,
              }}
            >
              {id.sfAccountId && <div>Account: {id.sfAccountId}</div>}
              {id.sfContactId && <div>Contact: {id.sfContactId}</div>}
            </div>
          </div>
        )}

        {/* Quick stats — cases/orders cells switch the center tab;
            products cell is non-clickable (no products tab in this
            layout). Mirrors A: 2-Column's clickable stat grid. */}
        <div style={s.statsGrid}>
          {data.stats.ytdSales !== undefined && (
            <div style={s.statCell}>
              <div style={s.statValue}>{formatCurrency(data.stats.ytdSales)}</div>
              <div style={s.statLabel}>YTD Sales</div>
            </div>
          )}
          <div
            role="button"
            title="Switch to Records tab"
            onClick={() => setCenterTab('records')}
            style={{ ...s.statCell, cursor: 'pointer' }}
          >
            <div style={{ ...s.statValue, color: data.stats.openCases > 0 ? c.red : c.text }}>
              {data.stats.openCases}
            </div>
            <div style={s.statLabel}>Open Cases</div>
          </div>
          <div
            role="button"
            title="Switch to Records tab"
            onClick={() => setCenterTab('records')}
            style={{ ...s.statCell, cursor: 'pointer' }}
          >
            <div style={s.statValue}>{data.stats.totalOrders}</div>
            <div style={s.statLabel}>Orders</div>
          </div>
          <div
            role="button"
            title="Switch to Records tab"
            onClick={() => setCenterTab('records')}
            style={{ ...s.statCell, cursor: 'pointer' }}
          >
            <div style={s.statValue}>{data.stats.registeredProducts}</div>
            <div style={s.statLabel}>Products</div>
          </div>
        </div>

        {/* Products — asset cards with activity indicators, matching
            A: 2-Column. Clicks are non-navigational here (this layout
            has no Products tab) but the card reads the same. */}
        {data.assets.length > 0 && (
          <div style={s.mergedSection}>
            <div style={s.sectionLabel}>Products ({data.assets.length})</div>
            {data.assets.map((a) => {
              const offlineDays = daysSince(a.lastConnected);
              const activity: {
                label: string;
                dot: string;
                fg: string;
              } = (() => {
                if (offlineDays == null) {
                  return { label: 'Not connected', dot: c.textMuted, fg: c.textSec };
                }
                if (offlineDays <= 7) {
                  return { label: 'Active', dot: c.green, fg: '#166534' };
                }
                return {
                  label: `Offline ${offlineDays}d`,
                  dot: c.red,
                  fg: '#991b1b',
                };
              })();
              return (
                <div
                  key={a.serialNumber}
                  style={{
                    display: 'flex',
                    gap: '10px',
                    padding: '8px 0',
                    borderBottom: `1px solid ${c.borderLight}`,
                  }}
                >
                  <div
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '8px',
                      background: `${c.amber}15`,
                      color: c.amber,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <ProductTag size={18} strokeWidth={2} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '12px',
                        fontWeight: 500,
                        color: c.text,
                      }}
                    >
                      <span
                        title={activity.label}
                        style={{
                          width: '7px',
                          height: '7px',
                          borderRadius: '50%',
                          background: activity.dot,
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {a.productName}
                      </span>
                      <span
                        style={{
                          fontSize: '10px',
                          fontWeight: 600,
                          color: activity.fg,
                          flexShrink: 0,
                        }}
                      >
                        {activity.label}
                      </span>
                    </div>
                    {a.nickname && (
                      <div style={{ fontSize: '11px', color: c.textMuted, marginTop: '1px' }}>
                        {a.nickname}
                      </div>
                    )}
                    {a.serialNumber && (
                      <div
                        style={{
                          fontSize: '10px',
                          color: c.textSec,
                          fontFamily: 'monospace',
                          marginTop: '2px',
                        }}
                      >
                        SN: {a.serialNumber}
                      </div>
                    )}
                    {a.deviceUuid && a.deviceUuid !== a.serialNumber && (
                      <div
                        style={{
                          fontSize: '10px',
                          color: c.textSec,
                          fontFamily: 'monospace',
                          marginTop: '2px',
                        }}
                      >
                        UUID: {a.deviceUuid}
                      </div>
                    )}
                    {a.categoryL1 && (
                      <div style={{ fontSize: '10px', color: c.textSec, marginTop: '2px' }}>
                        {[a.categoryL1, a.categoryL2, a.categorySeries, a.categoryModel]
                          .filter(Boolean)
                          .join(' > ')}
                      </div>
                    )}
                    {(() => {
                      const regDate =
                        a.registeredDate ?? a.purchaseDate ?? a.fleetEntryDate ?? null;
                      if (!regDate) return null;
                      return (
                        <div style={{ fontSize: '10px', color: c.textSec, marginTop: '2px' }}>
                          Registered: {timeAgo(regDate)}
                        </div>
                      );
                    })()}
                    {a.warrantyExpiration && (
                      <div style={{ fontSize: '10px', color: c.textSec, marginTop: '2px' }}>
                        Warranty:{' '}
                        <span
                          style={{
                            color: a.warrantyVoided ? c.red : a.warrantyPassed ? c.red : c.green,
                            fontWeight: 600,
                          }}
                        >
                          {a.warrantyVoided
                            ? 'Voided'
                            : a.warrantyPassed
                              ? `Expired ${timeAgo(a.warrantyExpiration)}`
                              : `Valid until ${timeAgo(a.warrantyExpiration)}`}
                        </span>
                      </div>
                    )}
                    {a.lastConnected && (
                      <div style={{ fontSize: '10px', color: c.textSec, marginTop: '2px' }}>
                        Last seen: {timeAgo(a.lastConnected)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Merged profiles footer */}
        {data.sourceProfiles.length > 1 && (
          <div style={s.mergedSection}>
            <div style={s.sectionLabel}>Merged profiles ({data.sourceProfiles.length})</div>
            {data.sourceProfiles.map((sp) => (
              <div
                key={sp.profileId}
                style={{
                  fontSize: '11px',
                  color: c.textMuted,
                  padding: '2px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <span
                  style={{
                    display: 'inline-block',
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: c.blue,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {sp.email || sp.phone || '—'}
                </span>
                <CustomerProfileLink
                  profileId={sp.profileId}
                  style={{ fontFamily: 'monospace', fontSize: '10px' }}
                >
                  {sp.profileId.slice(0, 8)}
                </CustomerProfileLink>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ===== CENTER COLUMN (tabbed) ===== */}
      <div style={s.timeline}>
        {/* Tabs — matches Customer360Panel: 8px 12px button padding,
            11px font, 8px horizontal padding on the bar, flush bottom
            border with a 2px blue underline on the active tab. */}
        <div
          style={{
            display: 'flex',
            gap: 0,
            borderBottom: `1px solid ${c.border}`,
            background: c.bg,
            flexShrink: 0,
            padding: '0 8px',
            position: 'sticky',
            top: 0,
            zIndex: 2,
          }}
        >
          {(
            [
              { key: 'timeline' as CenterTab, label: 'Timeline', count: events.length },
              {
                key: 'records' as CenterTab,
                label: 'Records',
                // Count mirrors A: 2-Column — sum of everything that
                // renders inside the combined Records tab.
                count:
                  data.cases.length +
                  data.orders.length +
                  data.shipments.length +
                  data.assets.length,
              },
            ] as const
          )
            .filter((t) => t.key === 'timeline' || t.count > 0)
            .map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  setCenterTab(tab.key);
                  setSelectedId(null);
                }}
                style={{
                  padding: '8px 12px',
                  fontSize: '11px',
                  fontWeight: 600,
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  color: centerTab === tab.key ? c.blue : c.textSec,
                  borderBottom:
                    centerTab === tab.key ? `2px solid ${c.blue}` : '2px solid transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                {tab.label}
                <span style={{ fontSize: '10px', fontWeight: 400, color: c.textMuted }}>
                  {tab.count}
                </span>
              </button>
            ))}
        </div>

        {/* Tab: Timeline */}
        {centerTab === 'timeline' && (
          <>
            {Array.from(grouped.entries()).map(([date, dateEvents]) => (
              <div key={date}>
                <div style={s.dateGroup}>{date}</div>
                {dateEvents.map((event) => {
                  // Shipment rows show their per-item lines inline (same
                  // shape as the Shipments tab) so an agent reading the
                  // timeline can see what's in each order without clicking
                  // through to the detail panel.
                  const shipmentItems =
                    event.type === 'shipment' && Array.isArray(event.detail.items)
                      ? (event.detail.items as ShipmentItem[])
                      : null;
                  return (
                    <div
                      key={event.id}
                      style={{
                        ...s.eventRow,
                        ...(selectedId === event.id ? s.eventRowSelected : {}),
                      }}
                      onClick={() => setSelectedId(selectedId === event.id ? null : event.id)}
                    >
                      <span style={s.eventTime}>{formatTime(event.timestamp)}</span>
                      <span style={{ ...s.eventDot, background: eventColors[event.type] }} />
                      <div style={s.eventContent}>
                        <div style={s.eventTitle}>{event.title}</div>
                        <div style={s.eventMeta}>
                          <span
                            style={{
                              ...s.eventBadge,
                              background: eventColors[event.type] + '20',
                              color: eventColors[event.type],
                            }}
                          >
                            {eventLabels[event.type]}
                          </span>
                          {event.status &&
                            (() => {
                              const sc = statusColors[event.status] || {
                                bg: '#f3f4f6',
                                fg: '#6b7280',
                              };
                              return (
                                <span style={{ ...s.eventBadge, background: sc.bg, color: sc.fg }}>
                                  {event.status}
                                </span>
                              );
                            })()}
                          {event.brand && (
                            <span
                              style={{ ...s.eventBadge, background: '#f3f4f6', color: '#6b7280' }}
                            >
                              Brand: {event.brand}
                            </span>
                          )}
                        </div>
                        {shipmentItems && (
                          <div style={{ marginTop: '6px' }}>
                            {shipmentItems.map((item, i) => (
                              <div
                                key={i}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  width: '100%',
                                  padding: '3px 0',
                                  fontSize: '12px',
                                }}
                              >
                                <span style={{ color: c.text }}>{item.productDescription}</span>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                  <span
                                    style={{
                                      color: c.textMuted,
                                      fontFamily: 'monospace',
                                      fontSize: '11px',
                                    }}
                                  >
                                    {item.productSku}
                                  </span>
                                  {/* Mirror the Search List logic: is_delivered →
                                      ship_date → pending. tracking_number is unreliable
                                      — sometimes empty even after shipment. */}
                                  {item.isDelivered ? (
                                    <span style={{ ...s.eventBadge, ...statusColors.Delivered }}>
                                      Delivered
                                    </span>
                                  ) : item.shipDate ? (
                                    <span style={{ ...s.eventBadge, ...statusColors.Delivered }}>
                                      Shipped
                                    </span>
                                  ) : (
                                    <span style={{ ...s.eventBadge, ...statusColors.Processing }}>
                                      Pending
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </>
        )}

        {/* Tab: Records — stacked sections matching A: 2-Column.
            Reuses the exact section components from Customer360Panel so
            Products / Cases / Shipments / Orders render identically
            across the two layouts. Section order mirrors Panel's
            "identify the grill, then the open issue, then how parts are
            moving" flow.
            TODO: Traeger-specific flow description and section order. */}
        {centerTab === 'records' && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              padding: '8px',
            }}
          >
            {data.assets.length > 0 && (
              <AssetsSection
                assets={data.assets}
                expandedItems={EMPTY_EXPANSION}
                toggleItem={handleRecordClick}
              />
            )}
            {data.cases.length > 0 && (
              <CasesSection
                cases={data.cases}
                expandedItems={EMPTY_EXPANSION}
                toggleItem={handleRecordClick}
              />
            )}
            {data.shipments.length > 0 && (
              <ShipmentsSection
                shipments={data.shipments}
                expandedItems={EMPTY_EXPANSION}
                toggleItem={handleRecordClick}
              />
            )}
            {data.orders.length > 0 && (
              <OrdersSection
                orders={data.orders}
                expandedItems={EMPTY_EXPANSION}
                toggleItem={handleRecordClick}
              />
            )}
          </div>
        )}
      </div>

      {/* ===== RIGHT DETAIL PANEL ===== */}
      <DetailPanel event={selectedEvent} />
    </div>
  );
}
